import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { DESTINATION_DEFAULT_FORM_VALUES } from "../../../../constants"
import { useGetInventoryShelvingOptions } from "../../../../hooks"
import { TransferType } from "../../constants"
import { useTransferPalletSelection } from "../../hooks/useTransferPalletSelection"
import { useTransferPalletSelectionMissingLotDetailsFill } from "../../hooks/useTransferPalletSelectionMissingLotDetailsFill"
import {
  type IntraTransferPalletSelectionFormValues,
  intraTransferPalletSelectionFormSchema,
} from "../../schemas/transfer"
import type { TransferFormPallet, TransferFormProductInventory } from "../../types"
import { getSelectedShelvingIds, toIntraTransferFormPallets } from "../../utils/transfer"

import { IntraTransferPalletDestinations } from "./IntraTransferPalletDestinations"
import { TransferInventoryHeader } from "./TransferInventoryHeader"
import { TransferPalletSelection } from "./TransferPalletSelection"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { InventoryPalletGroup, WarehouseArea } from "@/graphql/codegen/graphql"

interface IntraTransferPalletSelectionDialogProps {
  inventories: TransferFormProductInventory[]
  productId: number
  warehouseArea: WarehouseArea
  onClose: () => void
  onSubmit: (pallets: TransferFormPallet[]) => void
}

export const IntraTransferPalletSelectionDialog: FC<IntraTransferPalletSelectionDialogProps> = ({
  inventories,
  productId,
  warehouseArea,
  onClose,
  onSubmit,
}) => {
  const inventory = inventories.find((item) => item.product.productId === productId)!

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { product } = inventory
  const uomName = product.packageUsWeightUomDetails?.name ?? ""
  const form = useForm<IntraTransferPalletSelectionFormValues>({
    resolver: zodResolver(intraTransferPalletSelectionFormSchema),
    defaultValues: {
      pallets: inventory.pallets.map(({ destination, ...pallet }) => ({
        ...pallet,
        destination: destination ?? DESTINATION_DEFAULT_FORM_VALUES,
      })),
    },
    mode: "onChange",
  })

  const {
    control,
    formState: { isValid, isDirty },
  } = form

  const {
    fields: palletFields,
    append: appendPallet,
    remove: removePallet,
  } = useFieldArray({ control, name: "pallets" })

  const palletValues = useWatch({ control, name: "pallets" })
  const selectedLotIds = useMemo(
    () => new Set(palletFields.flatMap(({ lots }) => lots.map((lot) => lot.rawInventoryId))),
    [palletFields]
  )
  const selectedShelvingIds = useMemo(
    () => [
      ...getSelectedShelvingIds({ inventories, productId }),
      ...palletValues
        .map(({ destination }) => destination.shelving.warehouseShelvingId)
        .filter((id): id is string => id !== null),
    ],
    [inventories, productId, palletValues]
  )

  const {
    palletGroups,
    isFetching,
    sortBy,
    sortDirection,
    aisleCodes,
    handleSearchChange,
    handleSortChange,
    handleAisleCodesChange,
  } = useTransferPalletSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: warehouseArea.externalSystemId!,
  })

  useTransferPalletSelectionMissingLotDetailsFill({
    form,
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: warehouseArea.externalSystemId!,
  })

  const { aisleOptions } = useGetInventoryShelvingOptions({
    warehouseAreaId: warehouseArea.warehouseAreaId,
    includeUnavailable: true,
  })

  const handlePalletSelectionChange = (pallet: InventoryPalletGroup) => {
    const palletIndex = palletFields.findIndex((field) => field.referenceNbr === pallet.referenceNbr)

    if (palletIndex === -1) {
      appendPallet({ ...pallet, destination: DESTINATION_DEFAULT_FORM_VALUES })
    } else {
      removePallet(palletIndex)
    }
  }

  const handlePalletRemove = (index: number) => removePallet(index)

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = form.handleSubmit((formValues) => onSubmit(toIntraTransferFormPallets(formValues)))

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='transfer.pallet-selection-dialog'
    >
      <DialogHeader title='Select Pallets' onClose={handleCancel} />
      <TransferInventoryHeader product={product} warehouseArea={warehouseArea} />
      <FormProvider {...form}>
        <DialogContent className='flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-10 pb-10 pt-6'>
          <TransferPalletSelection
            palletGroups={palletGroups}
            isFetching={isFetching}
            transferType={TransferType.IntraWarehouse}
            selectedLotIds={selectedLotIds}
            sortBy={sortBy}
            sortDirection={sortDirection}
            aisleCodes={aisleCodes}
            aisleOptions={aisleOptions}
            uomName={uomName}
            onPalletSelectionChange={handlePalletSelectionChange}
            onSortChange={handleSortChange}
            onSearchChange={handleSearchChange}
            onAisleCodesChange={handleAisleCodesChange}
          />
          <IntraTransferPalletDestinations
            warehouseArea={warehouseArea}
            palletFields={palletFields}
            selectedShelvingIds={selectedShelvingIds}
            uomName={uomName}
            onPalletRemove={handlePalletRemove}
          />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='transfer.pallet-selection-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isValid}
          onClick={handleSubmit}
          data-testid='transfer.pallet-selection-dialog.submit-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
